import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BrandMark } from "@/components/Brand";
import { Users, CheckCircle2, XCircle, ArrowLeft, Loader2, RefreshCw, Search, CreditCard, ClipboardList, FlaskConical } from "lucide-react";

const STATUS_STYLES = {
  active: "bg-primary/10 text-primary", trialing: "bg-primary/10 text-primary",
  demo: "bg-violet-100 text-violet-700",
  past_due: "bg-amber-100 text-amber-700", canceled: "bg-destructive/10 text-destructive",
  unpaid: "bg-destructive/10 text-destructive", incomplete: "bg-muted text-muted-foreground",
  none: "bg-muted text-muted-foreground",
};
const WORKSPACE_OPTS = ["not_started", "provisioning", "active", "paused", "offboarded"];
const ONBOARDING_OPTS = ["not_started", "in_progress", "completed"];
const cap = (s) => (s || "").replace(/_/g, " ");

function StatusBadge({ status }) {
  return <span className={`text-xs font-medium px-2.5 py-1 rounded-full capitalize ${STATUS_STYLES[status] || "bg-muted text-muted-foreground"}`} data-testid={`cust-status-${status}`}>{cap(status) || "none"}</span>;
}
function Stat({ icon: Icon, label, value, testid }) {
  return (
    <Card className="rounded-3xl border-border shadow-soft p-6 flex items-center gap-4" data-testid={testid}>
      <div className="h-11 w-11 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0"><Icon className="h-5 w-5 text-primary" /></div>
      <div><p className="text-2xl font-heading leading-none">{value}</p><p className="text-sm text-muted-foreground mt-1">{label}</p></div>
    </Card>
  );
}
const fmtDate = (s) => { if (!s) return "—"; try { return new Date(s).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); } catch { return s; } };
const money = (n) => (n == null ? "—" : `$${Number(n).toFixed(0)}`);

export default function Admin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [demoEmail, setDemoEmail] = useState("");
  const [grantingDemo, setGrantingDemo] = useState(false);
  const [revoking, setRevoking] = useState(false);

  const load = () => api.get("/admin/customers").then((r) => setData(r.data)).catch(() => toast.error("Could not load customers.")).finally(() => setBusy(false));
  useEffect(() => { if (!loading && user?.is_admin) load(); }, [loading, user]);

  const sync = async () => {
    setSyncing(true);
    try { const r = await api.post("/admin/customers/sync"); toast.success(`Synced ${r.data.synced} customer${r.data.synced === 1 ? "" : "s"} from Stripe.`); await load(); }
    catch { toast.error("Sync failed."); } finally { setSyncing(false); }
  };

  const filtered = useMemo(() => {
    let list = data?.customers || [];
    if (q.trim()) { const ql = q.toLowerCase(); list = list.filter((c) => [c.full_name, c.business_name, c.email].some((v) => (v || "").toLowerCase().includes(ql))); }
    if (status !== "all") list = list.filter((c) => status === "active" ? ["active", "trialing"].includes(c.subscription_status) : status === "inactive" ? !["active", "trialing", "demo"].includes(c.subscription_status) : c.subscription_status === status);
    return list;
  }, [data, q, status]);

  const saveOps = async () => {
    setSaving(true);
    try {
      const r = await api.patch(`/admin/customers/${selected.id}`, {
        workspace_status: selected.workspace_status, onboarding_status: selected.onboarding_status, internal_notes: selected.internal_notes || "",
      });
      setData((d) => ({ ...d, customers: d.customers.map((c) => (c.id === r.data.id ? r.data : c)) }));
      setSelected(r.data); toast.success("Customer updated.");
    } catch { toast.error("Could not save changes."); } finally { setSaving(false); }
  };

  const grantDemo = async () => {
    const email = demoEmail.trim().toLowerCase();
    if (!email || !email.includes("@")) { toast.error("Enter a valid email."); return; }
    setGrantingDemo(true);
    try {
      await api.post("/admin/demo-access", { email });
      toast.success(`Demo access granted to ${email}. It’s marked as Demo (non-paying).`);
      setDemoEmail("");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not grant demo access."); }
    finally { setGrantingDemo(false); }
  };

  const revokeDemo = async (email) => {
    setRevoking(true);
    try {
      await api.post("/admin/demo-access/revoke", { email });
      toast.success(`Demo access revoked for ${email}.`);
      setSelected(null);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not revoke demo access."); }
    finally { setRevoking(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-background"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) return <Navigate to="/content" replace />;

  return (
    <div className="min-h-screen bg-background" data-testid="customers-page">
      <header className="border-b border-border bg-background/80 backdrop-blur-md sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3"><BrandMark className="h-10 w-10 object-contain" /><span className="font-heading text-lg">Customers</span></div>
          <div className="flex items-center gap-3">
            <Button onClick={sync} disabled={syncing} variant="outline" size="sm" className="rounded-full border-border gap-2" data-testid="sync-stripe-button">
              {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Sync from Stripe
            </Button>
            <button onClick={() => navigate("/content")} className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5" data-testid="customers-back-button"><ArrowLeft className="h-4 w-4" /> Back to app</button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          <Stat icon={Users} label="Total customers" value={busy ? "—" : data?.total ?? 0} testid="stat-total" />
          <Stat icon={CheckCircle2} label="Active subscriptions" value={busy ? "—" : data?.active_subscriptions ?? 0} testid="stat-active" />
          <Stat icon={FlaskConical} label="Demo (non-paying)" value={busy ? "—" : data?.demo ?? 0} testid="stat-demo" />
          <Stat icon={XCircle} label="Inactive / unpaid" value={busy ? "—" : data?.inactive ?? 0} testid="stat-inactive" />
        </div>

        <Card className="rounded-3xl border-border shadow-soft p-5 mb-6" data-testid="demo-access-card">
          <div className="flex items-center gap-2 mb-1">
            <FlaskConical className="h-4 w-4 text-violet-600" />
            <p className="font-heading text-base">Grant demo access</p>
          </div>
          <p className="text-sm text-muted-foreground mb-4">Give one email full app access as a <span className="font-medium text-foreground">Demo (non-paying)</span> account — no Stripe subscription is created. Access persists across logins until you change it.</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Input
              value={demoEmail}
              onChange={(e) => setDemoEmail(e.target.value)}
              placeholder="name@example.com"
              type="email"
              className="rounded-full h-11 flex-1"
              data-testid="demo-access-email"
            />
            <Button onClick={grantDemo} disabled={grantingDemo} className="rounded-full h-11 gap-2 bg-violet-600 hover:bg-violet-700 text-white" data-testid="demo-access-grant-button">
              {grantingDemo ? <Loader2 className="h-4 w-4 animate-spin" /> : <FlaskConical className="h-4 w-4" />} Grant demo access
            </Button>
          </div>
        </Card>

        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div className="relative flex-1">
            <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, business or email" className="rounded-full pl-9 h-11" data-testid="customer-search" />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="rounded-full h-11 w-full sm:w-52" data-testid="customer-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="demo">Demo</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
              <SelectItem value="past_due">Past due</SelectItem>
              <SelectItem value="canceled">Canceled</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Card className="rounded-3xl border-border shadow-soft overflow-hidden" data-testid="customers-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground border-b border-border bg-muted/40">
                  <th className="px-5 py-3 font-medium">Customer</th><th className="px-5 py-3 font-medium">Status</th>
                  <th className="px-5 py-3 font-medium">Setup</th><th className="px-5 py-3 font-medium">Plan</th>
                  <th className="px-5 py-3 font-medium">Next billing</th><th className="px-5 py-3 font-medium">Workspace</th><th className="px-5 py-3 font-medium">Onboarding</th>
                </tr>
              </thead>
              <tbody>
                {busy && <tr><td colSpan={7} className="px-5 py-10 text-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin inline" /> Loading…</td></tr>}
                {!busy && filtered.length === 0 && <tr><td colSpan={7} className="px-5 py-12 text-center text-muted-foreground">No customers found. Paying customers appear here automatically, or click “Sync from Stripe”.</td></tr>}
                {!busy && filtered.map((c) => (
                  <tr key={c.id} onClick={() => setSelected(c)} className="border-b border-border/60 last:border-0 hover:bg-muted/30 cursor-pointer" data-testid={`customer-row-${c.id}`}>
                    <td className="px-5 py-4"><div className="font-medium text-foreground">{c.full_name || "—"}</div><div className="text-muted-foreground text-xs">{c.business_name || "—"} · {c.email}</div></td>
                    <td className="px-5 py-4"><StatusBadge status={c.subscription_status} /></td>
                    <td className="px-5 py-4 text-muted-foreground capitalize">{cap(c.setup_fee_status)}</td>
                    <td className="px-5 py-4 text-muted-foreground">{money(c.monthly_amount)}/mo</td>
                    <td className="px-5 py-4 text-muted-foreground">{fmtDate(c.next_billing_date)}</td>
                    <td className="px-5 py-4 text-muted-foreground capitalize">{cap(c.workspace_status)}</td>
                    <td className="px-5 py-4 text-muted-foreground capitalize">{cap(c.onboarding_status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
        <p className="text-xs text-muted-foreground mt-4">Billing fields are read live from Stripe. Workspace status, onboarding and notes are managed by you.</p>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg rounded-3xl" data-testid="customer-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">{selected.full_name || selected.email}</DialogTitle>
                <p className="text-sm text-muted-foreground">{selected.business_name || "—"}</p>
              </DialogHeader>

              <div className="rounded-2xl bg-muted/40 p-4 mb-4">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground flex items-center gap-2 mb-3"><CreditCard className="h-3.5 w-3.5" /> Billing (from Stripe)</p>
                <div className="grid grid-cols-2 gap-y-2 gap-x-4 text-sm">
                  <div className="text-muted-foreground">Status</div><div><StatusBadge status={selected.subscription_status} /></div>
                  <div className="text-muted-foreground">Email</div><div className="truncate">{selected.email || "—"}</div>
                  <div className="text-muted-foreground">Phone</div><div>{selected.phone || "—"}</div>
                  <div className="text-muted-foreground">Setup fee</div><div className="capitalize">{cap(selected.setup_fee_status)}</div>
                  <div className="text-muted-foreground">Monthly plan</div><div>{money(selected.monthly_amount)}/mo</div>
                  <div className="text-muted-foreground">Payment status</div><div className="capitalize">{cap(selected.payment_status)}</div>
                  <div className="text-muted-foreground">Start date</div><div>{fmtDate(selected.start_date)}</div>
                  <div className="text-muted-foreground">Next billing</div><div>{fmtDate(selected.next_billing_date)}</div>
                  <div className="text-muted-foreground">Account</div><div>{selected.has_account ? "Has workspace login" : "No login yet"}</div>
                  <div className="text-muted-foreground">Stripe ID</div><div className="font-mono text-xs truncate">{selected.stripe_customer_id || "—"}</div>
                </div>
              </div>

              {selected.subscription_status === "demo" && (
                <div className="rounded-2xl border border-violet-200 bg-violet-50 p-4 mb-4" data-testid="demo-revoke-box">
                  <p className="text-sm text-violet-900 mb-3"><span className="font-semibold">Demo (non-paying) account.</span> Revoking ends their app access immediately. Their data is kept, and Stripe is not affected.</p>
                  <Button onClick={() => revokeDemo(selected.email)} disabled={revoking} variant="outline"
                    className="rounded-full border-violet-300 text-violet-700 hover:bg-violet-100 gap-2" data-testid="demo-revoke-button">
                    {revoking ? <Loader2 className="h-4 w-4 animate-spin" /> : <FlaskConical className="h-4 w-4" />} Revoke demo access
                  </Button>
                </div>
              )}

              <div className="rounded-2xl border border-border p-4">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground flex items-center gap-2 mb-3"><ClipboardList className="h-3.5 w-3.5" /> Operational (managed by you)</p>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <Label className="text-xs">Workspace status</Label>
                    <Select value={selected.workspace_status} onValueChange={(v) => setSelected((s) => ({ ...s, workspace_status: v }))}>
                      <SelectTrigger className="rounded-xl h-10 mt-1" data-testid="ops-workspace-status"><SelectValue /></SelectTrigger>
                      <SelectContent>{WORKSPACE_OPTS.map((o) => <SelectItem key={o} value={o} className="capitalize">{cap(o)}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Onboarding</Label>
                    <Select value={selected.onboarding_status} onValueChange={(v) => setSelected((s) => ({ ...s, onboarding_status: v }))}>
                      <SelectTrigger className="rounded-xl h-10 mt-1" data-testid="ops-onboarding-status"><SelectValue /></SelectTrigger>
                      <SelectContent>{ONBOARDING_OPTS.map((o) => <SelectItem key={o} value={o} className="capitalize">{cap(o)}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <Label className="text-xs">Internal notes</Label>
                <Textarea value={selected.internal_notes || ""} onChange={(e) => setSelected((s) => ({ ...s, internal_notes: e.target.value }))} placeholder="Private notes for handoff and follow-up." className="rounded-xl mt-1 min-h-[90px]" data-testid="ops-notes" />
                <Button onClick={saveOps} disabled={saving} className="rounded-full mt-4 bg-primary hover:bg-primary/90 text-primary-foreground gap-2" data-testid="ops-save-button">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save changes
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
